/**
 * A tela de resultados da busca — o que a home entrega depois da pergunta.
 *
 * Três formas, escolhidas pelo servidor: um identificador leva direto à entidade, um nome mostra
 * candidatos por tipo, uma frase mostra o recorte com total, chips e ações.
 *
 * A **barra de chips** não é enfeite: é a interface da interpretação. Um número como "7.823
 * pessoas" sem dizer COMO se chegou nele pede fé do operador, e fé é o contrário de investigação.
 * Cada condição vira um chip removível; o que o servidor não entendeu aparece em âmbar e não some
 * sozinho.
 *
 * Toda interação depois da primeira (paginar, remover chip, exportar) reenvia o PLANO, nunca a
 * frase — a página 2 é da mesma busca, e o CSV traz o mesmo recorte que está na tela.
 */
import { AlertTriangle, ArrowRight, Download, Search, Tag as TagIcon, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Pagina } from '@/components/ui/pagina';
import { Chip, Skeleton, Spinner } from '@/components/ui/misc';
import { Tabela, Tbody, Td, Th, Thead, Tr } from '@/components/ui/tabela';
import {
  baixarArquivo,
  buscar,
  marcarRecorte,
  type PlanoBusca,
  type RespostaBusca,
} from '@/lib/api';
import { rotaDe, rotaEntidade } from '@/lib/recursos';
import { rotuloTipo, rotuloTipoPlural } from '@/lib/recursos/rotulos';

const POR_PAGINA = 50;

export default function Busca() {
  const [params, setParams] = useSearchParams();
  const navegar = useNavigate();
  const pergunta = params.get('q') ?? '';

  const [texto, setTexto] = useState(pergunta);
  const [dados, setDados] = useState<RespostaBusca | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [pagina, setPagina] = useState(0);
  // Qual ação de lote está no ar. Sem isto o botão segue clicável durante uma marcação de 100 mil
  // linhas, e o operador — sem nenhum sinal de que algo acontece — clica de novo.
  const [acao, setAcao] = useState<'csv' | 'tag' | null>(null);

  const rodar = useCallback(
    async (corpo: { pergunta?: string; plano?: PlanoBusca; offset?: number }) => {
      setCarregando(true);
      const r = await buscar({ ...corpo, limite: POR_PAGINA });
      setCarregando(false);
      if (!r.ok) return void toast.error(r.erro);
      setDados(r.dados);
      // Um identificador com um único achado não merece uma tela de resultado: vai direto.
      if (r.dados.modo === 'identificador' && r.dados.achados?.length === 1) {
        const a = r.dados.achados[0];
        navegar(rotaEntidade(a.tipo, a.id), { replace: true });
      }
    },
    [navegar],
  );

  useEffect(() => {
    setTexto(pergunta);
    setPagina(0);
    if (pergunta.trim()) void rodar({ pergunta });
    else setDados(null);
  }, [pergunta, rodar]);

  function submeter(e: React.FormEvent) {
    e.preventDefault();
    const q = texto.trim();
    if (q) setParams({ q });
  }

  /** Remove uma condição e reexecuta — o plano é a fonte, não a frase. */
  function removerCondicao(i: number) {
    if (!dados?.plano) return;
    const plano: PlanoBusca = {
      ...dados.plano,
      condicoes: dados.plano.condicoes.filter((_, j) => j !== i),
      interpretacao: dados.plano.interpretacao.filter((_, j) => j !== i),
    };
    setPagina(0);
    void rodar({ plano });
  }

  function irPara(p: number) {
    if (!dados?.plano) return;
    setPagina(p);
    void rodar({ plano: dados.plano, offset: p * POR_PAGINA });
  }

  async function exportar() {
    if (!dados?.plano || acao) return;
    setAcao('csv');
    const r = await baixarArquivo('/cadastro/busca/exportar', 'busca.csv', 'POST', {
      plano: dados.plano,
    });
    setAcao(null);
    if (!r) toast.error('Não foi possível exportar.');
  }

  async function marcar() {
    if (!dados?.plano || acao) return;
    const nome = window.prompt('Nome da tag a aplicar em todo o recorte:');
    if (!nome?.trim()) return;
    setAcao('tag');
    const r = await marcarRecorte(dados.plano, nome.trim());
    setAcao(null);
    if (!r.ok) return void toast.error(r.erro);
    const { alcancados, marcados } = r.dados;
    // `marcados < alcancados` = parte já tinha a tag. Dizer isso evita que o operador ache que a
    // ação falhou e clique de novo.
    toast.success(
      marcados === alcancados
        ? `${marcados} marcados.`
        : `${marcados} marcados (${alcancados - marcados} já tinham a tag).`,
    );
  }

  return (
    <Pagina className="gap-4">
      {/* A caixa é o componente-assinatura do produto (manual §4): raio grande, respiro generoso,
          ícone em ciano. O vidro e a borda que acende são a camada nova — a matéria da superfície,
          não uma decoração aplicada por cima. */}
      <form onSubmit={submeter} className="relative z-[1] max-w-3xl">
        <div className="superficie-vidro borda-luz flex items-center gap-3 rounded-lg px-4 py-2.5">
          <Search className="size-4 shrink-0 text-accent" />
          <input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="um CPF, um telefone, uma placa — ou uma frase"
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[17px] text-text outline-none placeholder:text-text-tertiary focus:ring-0"
          />
          <button
            type="submit"
            aria-label="Buscar"
            className="halo-primary inline-flex size-8 shrink-0 items-center justify-center rounded-md bg-primary text-on-primary transition-colors hover:bg-primary-hover"
          >
            <ArrowRight className="size-4" />
          </button>
        </div>
      </form>

      {carregando && !dados && <Skeleton className="h-40 w-full" />}

      {dados?.modo === 'recorte' && (
        <>
          <div className="relative z-[1] flex flex-wrap items-baseline gap-2">
            {/* O total é o resultado da investigação — ganha o gradiente da marca, do rastro
                (ciano) à conexão (âmbar). A unidade fica em texto normal: o número é o dado. */}
            <span className="tipo-numero">{dados.total.toLocaleString('pt-BR')}</span>
            <span className="tipo-corpo-grande text-text-secondary">
              {rotuloTipoPlural(dados.alvo ?? 'pessoa', dados.total)}
            </span>
            {(dados.interpretacao ?? []).map((rotulo, i) => (
              <Chip key={rotulo} className="gap-1 border-accent/40">
                {rotulo}
                <button
                  type="button"
                  onClick={() => removerCondicao(i)}
                  aria-label={`Remover ${rotulo}`}
                  className="text-text-tertiary hover:text-danger"
                >
                  <X className="size-3" />
                </button>
              </Chip>
            ))}
          </div>

          {(dados.nao_entendido ?? []).length > 0 && (
            <p className="flex items-center gap-1.5 tipo-corpo-pequeno text-warning">
              <AlertTriangle className="size-4" />
              Não entendi: {dados.nao_entendido?.join(', ')} — o resultado ignora esse trecho.
            </p>
          )}

          <div className="relative z-[1] flex flex-wrap items-center gap-2">
            {dados.acoes?.lista && (
              <Button variant="secundario" onClick={() => navegar(rotaDe(dados.alvo ?? 'pessoa'))}>
                Abrir na lista
              </Button>
            )}
            {dados.acoes?.tag && (
              <Button variant="secundario" disabled={!!acao} onClick={() => void marcar()}>
                {acao === 'tag' ? <Spinner /> : <TagIcon className="size-4" />}
                {acao === 'tag' ? 'Marcando…' : 'Marcar com tag'}
              </Button>
            )}
            {dados.acoes?.csv && (
              <Button variant="secundario" disabled={!!acao} onClick={() => void exportar()}>
                {acao === 'csv' ? <Spinner /> : <Download className="size-4" />}
                {/* O CSV de um recorte grande demora: o rótulo tem que dizer o que está havendo,
                    não só girar. "Preparando" é o que o servidor está fazendo de fato. */}
                {acao === 'csv' ? 'Preparando…' : 'Exportar CSV'}
              </Button>
            )}
          </div>

          {/* Reexecutar mantém o resultado antigo na tela — trocá-lo por um esqueleto faria a
              página piscar a cada clique. O esmaecido diz "isto ainda é o anterior", e o
              `aria-busy` diz o mesmo a quem usa leitor de tela. */}
          <Card
            className="superficie-vidro relative z-[1] transition-opacity duration-[var(--dur-base)]"
            style={{ opacity: carregando ? 0.55 : 1 }}
            aria-busy={carregando}
          >
            <CardContent className="p-0">
              <Tabela>
                {/* `Thead` já monta a própria `<tr>` — envolver com `Tr` aninhava `<tr>` em
                    `<tr>`, HTML inválido que o React só denuncia no console. */}
                <Thead>
                  <Th>Nome</Th>
                  <Th>Tipo</Th>
                </Thead>
                <Tbody>
                  {(dados.itens ?? []).map((i) => (
                    <Tr key={i.id} onClick={() => navegar(rotaEntidade(i.tipo, i.id))}>
                      <Td>{i.rotulo}</Td>
                      <Td>{rotuloTipo(i.tipo)}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Tabela>
            </CardContent>
          </Card>

          {dados.total > POR_PAGINA && (
            <div className="flex items-center gap-2">
              <Button variant="secundario" disabled={pagina === 0} onClick={() => irPara(pagina - 1)}>
                Anterior
              </Button>
              <span className="tipo-corpo-pequeno text-text-tertiary">
                {pagina * POR_PAGINA + 1}–{Math.min((pagina + 1) * POR_PAGINA, dados.total)} de{' '}
                {dados.total.toLocaleString('pt-BR')}
              </span>
              <Button
                variant="secundario"
                disabled={(pagina + 1) * POR_PAGINA >= dados.total}
                onClick={() => irPara(pagina + 1)}
              >
                Próxima
              </Button>
            </div>
          )}
        </>
      )}

      {dados?.modo === 'texto' && (
        <div className="flex flex-col gap-4">
          {(dados.grupos ?? []).length === 0 && (
            <p className="tipo-corpo text-text-tertiary">
              Nada encontrado para «{dados.pergunta}».
            </p>
          )}
          {(dados.grupos ?? []).map((g) => (
            <Card key={g.tipo}>
              <CardContent className="flex flex-col gap-1 p-4">
                <p className="tipo-corpo-pequeno text-text-tertiary">
                  {rotuloTipo(g.tipo)} · {g.total}
                </p>
                {g.itens.map((i) => (
                  <button
                    key={i.id}
                    type="button"
                    onClick={() => navegar(rotaEntidade(i.tipo, i.id))}
                    className="w-fit text-left tipo-corpo text-text hover:text-accent"
                  >
                    {i.rotulo}
                  </button>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {dados?.modo === 'identificador' && (dados.achados ?? []).length === 0 && (
        <Card>
          <CardContent className="flex flex-col gap-2 p-6">
            <p className="tipo-corpo text-text">
              Não encontrei {dados.identificador?.tipo?.toUpperCase()}{' '}
              <span className="tipo-dado">{dados.pergunta}</span> na base.
            </p>
            {/* Não cair para busca textual é deliberado: listar 71 mil pessoas aqui seria lido
                como "o resultado do CPF que digitei". */}
            <p className="tipo-corpo-pequeno text-text-tertiary">
              O identificador é válido, mas nenhum cadastro o tem.
            </p>
          </CardContent>
        </Card>
      )}
    </Pagina>
  );
}
